import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { CancelIcon } from "@wit/mpesa-ui-components";
import StoryPreviewIcon from "../../../../shared/icons/story-preview.icon";

interface IProductRecommenderImagePreviewProps {
  uploadedImage: string | File | undefined;
  title?: { en: string; sw: string };
  description?: { en: string; sw: string };
  language: "en" | "sw";
}

const ProductRecommenderImagePreview: React.FC<IProductRecommenderImagePreviewProps> = ({
  uploadedImage,
  title = { en: "", sw: "" },
  description = { en: "", sw: "" },
  language,
}) => {
  const [t] = useTranslation();
  const [backgroundImage, setBackgroundImage] = React.useState<string>("");
  const [overlayImage, setOverlayImage] = React.useState<string | undefined>(undefined);

  /** Renders story preview icon */
  const rendersStoryPreviewIcon = () => {
    return <StoryPreviewIcon />;
  };

  useEffect(() => {
    // Fetch the background image
    fetch(process.env.PUBLIC_URL + "/images/product-recommendation-background.png")
      .then(response => response.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const base64data = reader.result as string;
          setBackgroundImage(base64data);
        };
      });

    // Convert File to base64 if needed and update the overlay image
    if (uploadedImage instanceof File) {
      const reader = new FileReader();
      reader.readAsDataURL(uploadedImage);
      reader.onloadend = () => {
        const base64data = reader.result as string;
        setOverlayImage(base64data);
      };
    } else if (typeof uploadedImage === "string") {
      setOverlayImage(uploadedImage);
    } else {
      setOverlayImage(undefined);
    }
  }, [uploadedImage]);

  // Determine title and description based on selected language
  const displayTitle = title[language] || "";
  const displayDescription = description[language] || "";

  return (
    <DivBackground background={backgroundImage}>
      {overlayImage && <OverlayImage src={overlayImage} />}
      <TextContainer>
        <Title>{displayTitle}</Title>
        <Description>{displayDescription}</Description>
      </TextContainer>
      <LearnMoreButton>Learn more</LearnMoreButton>
      <ShadowUpTopScreen />
      <StoryTimelineBar />
      <RowCustom>
        {rendersStoryPreviewIcon()}
        <CancelIcon width={"17px"} height={"17px"} style={{ stroke: "white" }} />
      </RowCustom>
    </DivBackground>
  );
};

const DivBackground = styled("div")<{ background: string }>`
  background: ${props => (props.background ? `url("${props.background}")` : "#e1f2ff")};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 24px;
`;

const OverlayImage = styled.img`
  position: absolute;
  width: 90%;
  height: 76%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ShadowUpTopScreen = styled("div")`
  position: absolute;
  width: 100%;
  height: 30%;
  top: 0;
  left: 0;
  right: 0;
  opacity: 0.2;
  background: linear-gradient(black, transparent);
`;

const StoryTimelineBar = styled("span")`
  width: 90%;
  height: 2px;
  opacity: 0.2;
  border-radius: 2.1px;
  background-color: white;
`;

const RowCustom = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 90%;
  margin-top: 8px;
`;

const LearnMoreButton = styled.button`
  position: absolute;
  bottom: 12%;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 30px;
  background-color: #dd0f1c;
  color: white;
  border: none;
  margin-bottom: 20px;
  border-radius: 4px;
  font-size: 11px;
`;

const TextContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 70%;
  max-width: 70%; /* Limit the width of the text container */
  transform: translateX(-50%);
  text-align: center;
  z-index: 2;
  color: white;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  padding: 0;
  text-align: left;
  word-wrap: break-word;
`;

const Description = styled.p`
  font-size: 14px;
  margin: 5px 0 0;
  text-align: left;
  word-wrap: break-word;
`;

export default ProductRecommenderImagePreview;
